import { useState, useEffect } from 'react';

interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
  functionality: boolean;
}

const defaultPreferences: CookiePreferences = {
  essential: true,
  analytics: false,
  marketing: false,
  functionality: false
};

export const useCookieConsent = () => {
  const [hasConsented, setHasConsented] = useState<boolean | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>(defaultPreferences);

  useEffect(() => {
    // Check if user has already given consent
    const consentGiven = localStorage.getItem('cookieConsent');
    const savedPreferences = localStorage.getItem('cookiePreferences');

    if (consentGiven === 'true' && savedPreferences) {
      setHasConsented(true);
      setPreferences(JSON.parse(savedPreferences));

      // Initialize analytics if consented
      const prefs = JSON.parse(savedPreferences);
      if (prefs.analytics) {
        initializeGoogleAnalytics();
      }
    } else if (consentGiven === 'false') {
      setHasConsented(false);
    } else {
      // Show banner after a delay for new users
      const timer = setTimeout(() => {
        setShowBanner(true);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, []);

  const initializeGoogleAnalytics = () => {
    // Add your Google Analytics initialization here
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('consent', 'update', {
        'analytics_storage': 'granted'
      });
    }
  };

  const acceptAll = () => {
    const allAcceptedPreferences: CookiePreferences = {
      essential: true,
      analytics: true,
      marketing: true,
      functionality: true
    };

    localStorage.setItem('cookieConsent', 'true');
    localStorage.setItem('cookiePreferences', JSON.stringify(allAcceptedPreferences));

    setHasConsented(true);
    setPreferences(allAcceptedPreferences);
    setShowBanner(false);
    setShowModal(false);

    // Initialize analytics
    initializeGoogleAnalytics();
  };

  const acceptEssential = () => {
    localStorage.setItem('cookieConsent', 'true');
    localStorage.setItem('cookiePreferences', JSON.stringify(defaultPreferences));

    setHasConsented(true);
    setPreferences(defaultPreferences);
    setShowBanner(false);
    setShowModal(false);
  };

  const customizeSettings = () => {
    setShowBanner(false);
    setShowModal(true);
  };

  const saveCustomPreferences = (customPrefs: CookiePreferences) => {
    localStorage.setItem('cookieConsent', 'true');
    localStorage.setItem('cookiePreferences', JSON.stringify(customPrefs));

    setHasConsented(true);
    setPreferences(customPrefs);
    setShowBanner(false);
    setShowModal(false);

    // Initialize analytics if enabled
    if (customPrefs.analytics) {
      initializeGoogleAnalytics();
    }
  };

  const openSettings = () => {
    setShowModal(true);
  };

  const resetConsent = () => {
    localStorage.removeItem('cookieConsent');
    localStorage.removeItem('cookiePreferences');
    setHasConsented(null);
    setPreferences(defaultPreferences);
    setShowBanner(true);
  };

  return {
    hasConsented,
    showBanner,
    showModal,
    preferences,
    acceptAll,
    acceptEssential,
    customizeSettings,
    saveCustomPreferences,
    openSettings,
    resetConsent,
    setShowModal
  };
};